import React, { type ComponentType } from 'react';
import flow from 'lodash/flow';

import memoizeOne from 'memoize-one';
import { SmartCardProvider } from '@atlaskit/link-provider';
import withLinkActions from '@atlassian/jira-common-navigation/src/with-link-actions';
import { ff } from '@atlassian/jira-feature-flagging';
import withMediaProvider from '@atlassian/jira-issue-media-provider';
import {
	useSmartCardProductType,
	useSmartCardIsAIEnabled,
} from '@atlassian/jira-linking-platform-utils';
import { useProfileCardProvider } from '@atlassian/jira-profilecard-provider';
import { useAccountId, useCloudId } from '@atlassian/jira-tenant-context-controller';
import { getEnableFlexibleBlockCardInIssueEditor } from './feature-flags';
import type { ContainerProps, WrappedProps } from './types';
import RendererView from './view';

const getSmartCardProviderFeatureFlags = memoizeOne(() => ({
	enableFlexibleBlockCard: getEnableFlexibleBlockCardInIssueEditor(),
}));

const WrappedRendererView: ComponentType<WrappedProps> = flow(
	withMediaProvider,
	withLinkActions,
)(RendererView);

const Renderer = (props: ContainerProps) => {
	const loggedInUserAccountId = useAccountId();
	const cloudId = useCloudId();
	const profileCardProvider = useProfileCardProvider();

	const smartCardProviderFF = getSmartCardProviderFeatureFlags();
	const smartCardProviderProduct = useSmartCardProductType();
	const isAIFeatureEnabled = useSmartCardIsAIEnabled();

	return (
		<SmartCardProvider
			featureFlags={smartCardProviderFF}
			{...(ff('platform.linking-platform.smart-card.hover-card-ai-summaries') && {
				product: smartCardProviderProduct,
				isAdminHubAIEnabled: isAIFeatureEnabled,
			})}
		>
			<WrappedRendererView
				{...props}
				cloudId={cloudId}
				loggedInUserAccountId={loggedInUserAccountId}
				profileCardProvider={profileCardProvider}
			/>
		</SmartCardProvider>
	);
};

export default Renderer;
